import { App, Button, Flex, Form } from 'antd';
import { FC, memo, useState } from 'react';
import { usePermission } from 'hooks';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { updateDoctor } from 'services/api/doctors';

import { useDoctorProfile } from 'pages/DoctorProfile/DoctorProfileContext';
import { showErrorMessage } from 'utils/showErrorMessage';
import { pickLocale } from 'utils/pickLocale';
import { CKEditor, OrganizationResult, Private } from 'components/shared';
import useDoctorProfileCache from 'hooks/useDoctorProfileCache';

interface IProps {
  id: string;
  lang: string;
  allowToEdit?: boolean;
}

const ProfileDetails: FC<IProps> = ({ id, lang, allowToEdit }) => {
  const { checkUserPermission } = usePermission();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { profile, validateProfile } = useDoctorProfileCache({ id, lang });
  const { refresh } = useDoctorProfile();
  const [isUpdating, setIsUpdating] = useState(false);

  const onFinish = (values: { about: string }) => {
    validateProfile(async () => {
      const doctorId = id || profile?.id;
      if (!doctorId) return;

      try {
        setIsUpdating(true);
        await updateDoctor(doctorId, { about: values?.about, lang });
        message.success('Successfully saved');
        refresh();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsUpdating(false);
      }
    });
  };

  const hasProfileUpdatePermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.update
  );

  return (
    <OrganizationResult>
      <Form
        form={form}
        layout="vertical"
        size="large"
        disabled={!hasProfileUpdatePermission}
        initialValues={{
          // profile.about is a per-locale jsonb map ({ en, ru, de, ... }).
          // Pick the active-lang string so CKEditor gets a string; the
          // save path already forwards `lang` so BE merges the returned
          // string back into the right jsonb slot.
          about: pickLocale(profile?.about, lang)
        }}
        onFinish={onFinish}
      >
        <Form.Item
          name="about"
          label="About doctor"
          rules={[{ required: true }]}
        >
          <CKEditor disabled={!allowToEdit} />
        </Form.Item>
        <Flex justify="end" gap={12}>
          <Private
            route={PERMISSION_ROUTES.doctors}
            action={PERMISSION_ACTIONS.update}
          >
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              loading={isUpdating}
              disabled={!allowToEdit}
            >
              Save
            </Button>
          </Private>
        </Flex>
      </Form>
    </OrganizationResult>
  );
};

export default memo(ProfileDetails);
